import type { FormikTouched, FormikValues } from "formik";
import { getIn, useFormikContext } from "formik";
import { cloneDeepWith, isObjectLike } from "lodash";

import type { Paths } from "@citrine/utils/types";

function isInputEvent(
  e: React.KeyboardEvent<HTMLElement>
): e is React.KeyboardEvent<HTMLTextAreaElement> {
  return e.target instanceof Element && /^input$/i.test(e.target.tagName);
}

function isEnterEvent(e: React.KeyboardEvent<HTMLElement>) {
  return e.key === "Enter";
}

export function isInputEnterEvent(e: React.KeyboardEvent<HTMLElement>) {
  return isEnterEvent(e) && isInputEvent(e);
}

export function preventEnterCallback(e: React.KeyboardEvent<HTMLElement>) {
  // if event was from a single input in a form, we don't want it to auto-submit
  if (isInputEnterEvent(e)) {
    e.preventDefault();
  }
}

export function useValidateOnChange(validateOnComponent: boolean | undefined) {
  const { validateOnBlur, validateOnChange } = useFormikContext();

  /**
   * first, respect the provided value. if no value is provided, we should only
   * perform control-level validation if form-level validation is enabled.
   *
   * if both validateOnBlur and validateOnChange are false, then live validation
   * is disabled and should remain so for the control.
   */
  return validateOnComponent ?? (validateOnBlur || validateOnChange);
}

export function getNestedArrayFieldName<FormShape, NestedFormField>(
  name: keyof FormShape & string,
  index: number,
  field: keyof NestedFormField & string
) {
  return `${name}[${index}].${field}`;
}

/**
 * Useful for marking all values in a form as touched on initialization or after
 * running validation without submission.
 */
export function valuesToTouchedShape<Values extends FormikValues>(
  values: Values
): FormikTouched<Values> {
  return cloneDeepWith(values, (value) =>
    isObjectLike(value) && Object.keys(value).length ? undefined : true
  );
}

export const getFormikErrorMessage = <Shape>(
  shape: Shape,
  key: Paths<Shape, string>
): string | undefined => getIn(shape, key);
